// Constantes partagées du domaine Veille Qualiopi.
// Source unique pour : métadonnées des types (indicateur/titre/desc),
// libellés/icônes de statut et normalisation des statuts.

// Métadonnées par type de veille.
// Référentiel National Qualité (Qualiopi) : indicateur 23 = veille légale et
// réglementaire, 24 = évolutions des compétences/métiers/emplois,
// 25 = innovations pédagogiques et technologiques, 26 = accueil et formation
// des publics en situation de handicap (expertises, outils et réseaux).
// (Historique : 23 et 25 étaient inversés dans l'application — corrigé.)
// `rh` est une veille interne SUPPLÉMENTAIRE (pas un indicateur Qualiopi numéroté) :
// elle porte un `badge` libre ('RH') au lieu d'un `indicator`, et `adminOnly: true`
// la réserve aux administrateurs (carte du hub, page détail et endpoints API filtrés).
// Les `title`/`desc` divergent volontairement entre la page hub (cards de Veille.vue)
// et la page détail (TYPE_META de VeilleReglementaire.vue) : on conserve les deux
// variantes telles qu'elles existaient dans chaque fichier.
export const VEILLE_TYPES = {
  reglementaire: {
    type: 'reglementaire',
    indicator: 23,
    // Variante page détail (ex-TYPE_META de VeilleReglementaire.vue)
    title: 'Veille réglementaire',
    desc: 'Veille sur les évolutions législatives et réglementaires impactant nos formations.',
    // Variante page hub (ex-cards de Veille.vue)
    hubTitle: 'Veille réglementaire',
    hubDesc: 'Veille sur les textes officiels (arrêtés, décrets, normes, recommandations) impactant les formations.',
  },
  innovations: {
    type: 'innovations',
    indicator: 25,
    title: 'Innovations pédagogiques',
    desc: 'Veille sur les nouvelles méthodes, outils et technologies de formation.',
    hubTitle: 'Innovations pédagogiques et technologiques',
    hubDesc: 'Veille sur les nouveaux outils, méthodes et technologies de formation (VR, mannequins connectés, LMS, IA…).',
  },
  competences: {
    type: 'competences',
    indicator: 24,
    title: 'Évolution des compétences',
    desc: 'Veille sur les évolutions des secteurs d\'intervention d\'Alertis.',
    hubTitle: 'Évolutions des compétences, métiers et emplois',
    hubDesc: 'Veille sur les évolutions des secteurs d\'intervention d\'Alertis et l\'exploitation de ces enseignements.',
  },
  handicap: {
    type: 'handicap',
    indicator: 26,
    title: 'Veille handicap',
    desc: 'Veille sur l\'accueil et la formation des publics en situation de handicap (accessibilité, compensation, référent handicap).',
    hubTitle: 'Accueil des publics en situation de handicap',
    hubDesc: 'Veille sur l\'accueil, l\'accompagnement et la formation des personnes en situation de handicap (accessibilité, compensation, AGEFIPH/FIPHFP, réseau de partenaires).',
  },
  veriflamme: {
    type: 'veriflamme',
    // Veille d'entreprise sœur (pas un indicateur Qualiopi) : badge libre,
    // visible par tous, affichée dans la section « Veille entreprises » du hub.
    indicator: null,
    badge: 'VF',
    entreprise: true,
    title: 'Veille Vériflamme',
    desc: 'Veille réglementaire et normative sur les extincteurs (vente, installation, maintenance) pour Vériflamme.',
    hubTitle: 'Veille Vériflamme — Extincteurs',
    hubDesc: 'Veille sur les textes et normes extincteurs (NF EN 3, NF S 61-919, APSAD R4, obligations ERP et Code du travail) : vente, installation et maintenance.',
  },
  sauvdefib: {
    type: 'sauvdefib',
    indicator: null,
    badge: 'SD',
    entreprise: true,
    title: 'Veille Sauvdéfib',
    desc: 'Veille réglementaire sur les défibrillateurs (DAE) — vente, maintenance, location — pour Sauvdéfib.',
    hubTitle: 'Veille Sauvdéfib — Défibrillateurs',
    hubDesc: 'Veille sur la réglementation DAE (obligations d\'équipement des ERP, maintenance des dispositifs médicaux, base Géo\'DAE) : vente, maintenance et location.',
  },
  rh: {
    type: 'rh',
    // Pas d'indicateur Qualiopi numéroté : on affiche le badge libre ci-dessous.
    indicator: null,
    badge: 'RH',
    adminOnly: true,
    title: 'Veille réglementation RH',
    desc: 'Veille sur les évolutions législatives RH (droit du travail, conventions collectives, obligations employeur) impactant l\'organisme et ses formations.',
    hubTitle: 'Veille réglementation RH',
    hubDesc: 'Veille sur les textes officiels RH (Code du travail, conventions collectives, accords, jurisprudence sociale) impactant l\'organisme de formation.',
  },
}

// Métadonnées des types pour la page détail (ex-TYPE_META de VeilleReglementaire.vue).
export const TYPE_META = {
  reglementaire: {
    title: VEILLE_TYPES.reglementaire.title,
    indicator: VEILLE_TYPES.reglementaire.indicator,
    desc: VEILLE_TYPES.reglementaire.desc,
  },
  innovations: {
    title: VEILLE_TYPES.innovations.title,
    indicator: VEILLE_TYPES.innovations.indicator,
    desc: VEILLE_TYPES.innovations.desc,
  },
  competences: {
    title: VEILLE_TYPES.competences.title,
    indicator: VEILLE_TYPES.competences.indicator,
    desc: VEILLE_TYPES.competences.desc,
  },
  handicap: {
    title: VEILLE_TYPES.handicap.title,
    indicator: VEILLE_TYPES.handicap.indicator,
    desc: VEILLE_TYPES.handicap.desc,
  },
  veriflamme: {
    title: VEILLE_TYPES.veriflamme.title,
    indicator: VEILLE_TYPES.veriflamme.indicator,
    badge: VEILLE_TYPES.veriflamme.badge,
    desc: VEILLE_TYPES.veriflamme.desc,
  },
  sauvdefib: {
    title: VEILLE_TYPES.sauvdefib.title,
    indicator: VEILLE_TYPES.sauvdefib.indicator,
    badge: VEILLE_TYPES.sauvdefib.badge,
    desc: VEILLE_TYPES.sauvdefib.desc,
  },
  rh: {
    title: VEILLE_TYPES.rh.title,
    indicator: VEILLE_TYPES.rh.indicator,
    badge: VEILLE_TYPES.rh.badge,
    desc: VEILLE_TYPES.rh.desc,
  },
}

// Cartes de la page hub, indicateurs Qualiopi en ordre croissant (23, 24, 25, 26).
export const VEILLE_HUB_CARDS = [
  {
    type: 'reglementaire',
    indicator: VEILLE_TYPES.reglementaire.indicator,
    title: VEILLE_TYPES.reglementaire.hubTitle,
    desc: VEILLE_TYPES.reglementaire.hubDesc,
  },
  {
    type: 'competences',
    indicator: VEILLE_TYPES.competences.indicator,
    title: VEILLE_TYPES.competences.hubTitle,
    desc: VEILLE_TYPES.competences.hubDesc,
  },
  {
    type: 'innovations',
    indicator: VEILLE_TYPES.innovations.indicator,
    title: VEILLE_TYPES.innovations.hubTitle,
    desc: VEILLE_TYPES.innovations.hubDesc,
  },
  {
    type: 'handicap',
    indicator: VEILLE_TYPES.handicap.indicator,
    title: VEILLE_TYPES.handicap.hubTitle,
    desc: VEILLE_TYPES.handicap.hubDesc,
  },
  // Veilles des entreprises sœurs (Vériflamme, Sauvdéfib) : visibles par tous,
  // affichées dans une section « Veille entreprises » distincte des cartes Qualiopi.
  {
    type: 'veriflamme',
    indicator: VEILLE_TYPES.veriflamme.indicator,
    badge: VEILLE_TYPES.veriflamme.badge,
    entreprise: VEILLE_TYPES.veriflamme.entreprise,
    title: VEILLE_TYPES.veriflamme.hubTitle,
    desc: VEILLE_TYPES.veriflamme.hubDesc,
  },
  {
    type: 'sauvdefib',
    indicator: VEILLE_TYPES.sauvdefib.indicator,
    badge: VEILLE_TYPES.sauvdefib.badge,
    entreprise: VEILLE_TYPES.sauvdefib.entreprise,
    title: VEILLE_TYPES.sauvdefib.hubTitle,
    desc: VEILLE_TYPES.sauvdefib.hubDesc,
  },
  // Veille RH (admin-only) : affichée en dernier, masquée pour les commerciaux.
  {
    type: 'rh',
    indicator: VEILLE_TYPES.rh.indicator,
    badge: VEILLE_TYPES.rh.badge,
    adminOnly: VEILLE_TYPES.rh.adminOnly,
    title: VEILLE_TYPES.rh.hubTitle,
    desc: VEILLE_TYPES.rh.hubDesc,
  },
]

// Types de veille réservés aux administrateurs : la carte du hub, la page détail
// et les endpoints API correspondants sont filtrés selon le rôle. Source unique
// dérivée de `adminOnly` sur VEILLE_TYPES (front) — le back a son propre miroir.
export const ADMIN_ONLY_VEILLE_TYPES = new Set(
  Object.values(VEILLE_TYPES).filter((t) => t.adminOnly).map((t) => t.type)
)

// True si le type de veille est réservé aux administrateurs.
export function isAdminOnlyVeilleType(type) {
  return ADMIN_ONLY_VEILLE_TYPES.has(type)
}

// Statuts métier. Le statut legacy `validated` est normalisé en `to_send`.
export const VEILLE_STATUS = {
  pending: 'pending',
  to_send: 'to_send',
  sent: 'sent',
  rejected: 'rejected',
}

// Libellés d'affichage par statut normalisé. `to_send` s'affiche « Validée » :
// depuis l'envoi automatique différé, une fiche validée part toute seule —
// « à envoyer » suggérait une action manuelle restante.
export const STATUS_LABELS = {
  pending: 'En attente',
  to_send: 'Validée',
  sent: 'Envoyée',
  rejected: 'Rejetée',
}

// Icônes Font Awesome par statut normalisé.
export const STATUS_ICONS = {
  pending: 'fa-clock',
  to_send: 'fa-check-circle',
  sent: 'fa-paper-plane',
  rejected: 'fa-ban',
}

// Variante du composant Badge par statut normalisé.
export const STATUS_BADGE_VARIANT = {
  pending: 'danger',
  to_send: 'warning',
  sent: 'success',
  rejected: 'secondary',
}

// Verbe d'action affiché dans le toast après changement de statut.
export const STATUS_ACTION_VERBS = {
  to_send: 'validée',
  pending: 'remise en attente',
  rejected: 'rejetée',
}

// Formats acceptés à l'import d'un document de veille (PDF tel quel ; une
// image est enveloppée dans un PDF A4 côté serveur). Doit rester aligné sur
// l'allowlist serveur (server/routes/veille.routes.js).
export const VEILLE_DOCUMENT_MIMES = ['application/pdf', 'image/jpeg', 'image/png']
export const VEILLE_DOCUMENT_ACCEPT = VEILLE_DOCUMENT_MIMES.join(',')

// Normalise les anciens 'validated' en 'to_send' (rétro-compat) et les
// statuts absents en 'pending'.
export function normalizeStatus(s) {
  if (s === 'validated') return 'to_send'
  return s || 'pending'
}

// Renvoie le libellé d'affichage pour un statut (normalisé en amont).
export function statusLabel(s) {
  const n = normalizeStatus(s)
  return STATUS_LABELS[n] || STATUS_LABELS.pending
}

// Renvoie l'icône Font Awesome pour un statut (normalisé en amont).
export function statusIcon(s) {
  const n = normalizeStatus(s)
  return STATUS_ICONS[n] || STATUS_ICONS.pending
}

// Renvoie la variante du composant Badge pour un statut.
export function statusBadgeVariant(s) {
  return STATUS_BADGE_VARIANT[normalizeStatus(s)] || 'secondary'
}

// Libellés des sources de destinataires (aperçus de diffusion) — source
// unique pour SendVeilleDialog (singulier, étiquette par destinataire) et
// VeilleDiffusionStep (pluriel, compteurs/répartition).
export const DIFFUSION_SOURCE_LABELS_PLURIEL = {
  formateur: 'formateurs inscrits sur le site',
  'formateur-historique': 'formateurs historiques',
  user: 'comptes back-office',
  employe: 'employés Alertis',
  admin: 'administrateurs',
  extra: 'adresses libres',
  copie: "copie à la boîte d'envoi",
}

export const DIFFUSION_SOURCE_LABELS_SINGULIER = {
  formateur: 'formateur',
  // Legacy : encore présent dans l'audit `sentTo` des fiches envoyées avant
  // la fusion des cases formateurs.
  'formateur-inscrit': 'formateur inscrit (non validé)',
  'formateur-historique': 'formateur historique (hors Global Prévention)',
  user: 'back-office',
  employe: 'employé Alertis',
  admin: 'administrateur',
  extra: 'adresse libre',
  copie: "copie à la boîte d'envoi",
}

// Message email par défaut transmis aux formateurs pour une fiche de veille.
// Centralisé ici (contenu métier) plutôt qu'embarqué dans SendVeilleDialog.
export function buildVeilleEmailMessage(entry) {
  if (!entry) return ''
  // Les fiches entreprises/RH ne sont pas des fiches Qualiopi : libellé adapté.
  const label = entry.type === 'veriflamme' ? 'veille Vériflamme (extincteurs)'
    : entry.type === 'sauvdefib' ? 'veille Sauvdéfib (défibrillateurs)'
    : entry.type === 'rh' ? 'veille réglementation RH'
    : entry.type === 'handicap' ? 'veille handicap (accueil des publics en situation de handicap)'
    : 'veille Qualiopi'
  return `Bonjour,

Nous vous transmettons une nouvelle fiche de ${label} sur le sujet suivant :

« ${entry.title} »

${entry.summary || ''}

Référence officielle : ${entry.officialRef || ''}

Cordialement,
Alertis Formation`
}
